/**
 * VoiceCommand — Executive Command Center for Voice AURI.
 * Tabs: Overview · Sessions · Configuration · Voice Preview (A/B)
 */
import { useEffect, useState, useCallback } from "react";
import { RefreshCw, Mic, Play, Square, DollarSign, Clock, Users, AlertTriangle, MessageSquare, Sparkles, PhoneCall } from "lucide-react";
import { Eyebrow, EditorialHeading } from "../../components/brand";
import { apiFetch } from "../../lib/api";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "recommendations", label: "Recommendations" },
  { id: "sessions", label: "Sessions" },
  { id: "config",   label: "Configuration" },
  { id: "voices",   label: "Voice Preview" },
];

function fmtSec(s) { const n = Math.round(s || 0); const m = Math.floor(n/60); return m ? `${m}m ${n%60}s` : `${n}s`; }
function fmtUsd(n) { return `$${(Number(n)||0).toFixed(2)}`; }

export default function VoiceCommand() {
  const [tab, setTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [voices, setVoices] = useState([]);
  const [detail, setDetail] = useState(null);   // {session, transcript, summary, lead}
  const [previewVoice, setPreviewVoice] = useState("sage");
  const [previewState, setPreviewState] = useState("idle");
  const [previewPc, setPreviewPc] = useState(null);
  const [previewAudio, setPreviewAudio] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, s, c, v] = await Promise.all([
        apiFetch("/api/voice/admin/analytics"),
        apiFetch("/api/voice/admin/sessions?limit=100"),
        apiFetch("/api/voice/admin/config"),
        apiFetch("/api/voice/admin/voices"),
      ]);
      setAnalytics(a); setSessions(s || []); setCfg(c); setVoices(v?.voices || []);
    } catch (e) { console.warn("voice-cmd load", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try { const d = await apiFetch(`/api/voice/admin/sessions/${id}`); setDetail(d); }
    catch (e) { console.warn(e); }
  };

  const patchCfg = async (patch) => {
    try {
      const next = await apiFetch("/api/voice/admin/config", { method: "PATCH", body: patch });
      setCfg(next);
    } catch (e) { console.warn(e); }
  };

  const toggleKillSwitch = async (v) => {
    try { await apiFetch("/api/voice/admin/kill-switch", { method: "POST", body: { triggered: v } }); await load(); }
    catch (e) { console.warn(e); }
  };

  const runPreview = async (voiceId) => {
    try {
      setPreviewState("connecting");
      const mint = await apiFetch("/api/voice/admin/preview", { method: "POST", body: { voice: voiceId } });
      const pc = new RTCPeerConnection();
      const audio = document.createElement("audio");
      audio.autoplay = true; audio.playsInline = true;
      pc.ontrack = (ev) => { audio.srcObject = ev.streams[0]; };
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      pc.createDataChannel("oai-events");
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const sdpRes = await fetch(`${mint.metadata.webrtc_url}?model=${encodeURIComponent(mint.model)}`, {
        method: "POST", body: offer.sdp,
        headers: { Authorization: `Bearer ${mint.client_secret.value}`, "Content-Type": "application/sdp" },
      });
      const answerSdp = await sdpRes.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
      setPreviewPc(pc); setPreviewAudio({ audio, stream });
      setPreviewState("live");
    } catch (e) {
      console.warn("preview error", e); setPreviewState("error");
    }
  };
  const stopPreview = () => {
    try { previewPc?.close(); } catch (e) { void e; }
    try { previewAudio?.stream?.getTracks?.().forEach((t) => t.stop()); } catch (e) { void e; }
    try { previewAudio?.audio?.remove(); } catch (e) { void e; }
    setPreviewPc(null); setPreviewAudio(null); setPreviewState("idle");
  };

  return (
    <div className="space-y-8" data-testid="voice-command-panel">
      <div className="flex items-center justify-between">
        <div>
          <Eyebrow className="text-altura-gold-dark">Voice AURI · Executive View</Eyebrow>
          <EditorialHeading size="md" className="mt-2">Voice Command Center</EditorialHeading>
        </div>
        <div className="flex items-center gap-3">
          {analytics?.today?.kill_switch ? (
            <button onClick={() => toggleKillSwitch(false)} className="btn-espresso" data-testid="voice-kill-resume">
              <Play className="h-3.5 w-3.5" /> Resume Voice
            </button>
          ) : (
            <button onClick={() => toggleKillSwitch(true)} className="btn-ghost-espresso" data-testid="voice-kill-pause">
              <Square className="h-3.5 w-3.5" /> Pause Voice
            </button>
          )}
          <button onClick={load} className="btn-ghost-espresso" data-testid="voice-cmd-refresh"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-altura-espresso/10">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm border-b-2 transition-colors ${tab === t.id ? "border-altura-gold text-altura-espresso" : "border-transparent text-altura-charcoal/60 hover:text-altura-espresso"}`}
            data-testid={`voice-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === "overview" && (
        <div className="grid gap-5 md:grid-cols-4">
          <StatCard label="Sessions today"   value={analytics?.today?.sessions ?? 0} icon={PhoneCall} />
          <StatCard label="Audio (today)"    value={fmtSec(analytics?.today?.audio_seconds)} icon={Clock} />
          <StatCard label="Spend today"      value={fmtUsd(analytics?.today?.cost_usd)}
                    accent={(analytics?.today?.cost_usd || 0) >= 0.7 * (cfg?.daily_usage_cap_usd || 10) ? "warn" : "ok"}
                    icon={DollarSign} sub={`Cap: ${fmtUsd(cfg?.daily_usage_cap_usd || 10)}`} />
          <StatCard label="Leads captured"   value={analytics?.lifetime?.leads_captured ?? 0} icon={Users} />
          <StatCard label="Total sessions"   value={analytics?.lifetime?.sessions ?? 0} icon={MessageSquare} />
          <StatCard label="Handoffs"         value={analytics?.lifetime?.handoffs ?? 0} icon={Sparkles} />
          <StatCard label="Kill switch"      value={analytics?.today?.kill_switch ? "ACTIVE" : "Off"}
                    accent={analytics?.today?.kill_switch ? "danger" : "ok"} icon={AlertTriangle} />
          <StatCard label="Provider"         value={cfg?.provider || "openai_realtime"} icon={Mic} sub={`Voice: ${cfg?.default_voice || "sage"}`} />
        </div>
      )}

      {/* RECOMMENDATIONS (Phase 7) */}
      {tab === "recommendations" && (
        <div className="space-y-6" data-testid="voice-reco-admin">
          <div className="grid gap-5 md:grid-cols-4">
            <StatCard label="Total offered"     value={analytics?.recommendations?.total_offered ?? 0} />
            <StatCard label="Accepted"          value={analytics?.recommendations?.accepted ?? 0}
                      sub={`${analytics?.recommendations?.acceptance_rate_pct ?? 0}% acceptance`} />
            <StatCard label="Click-through"     value={analytics?.recommendations?.clicked ?? 0}
                      sub={`${analytics?.recommendations?.click_through_rate_pct ?? 0}% CTR`} />
            <StatCard label="Consultations"     value={analytics?.recommendations?.consultation ?? 0} />
            <StatCard label="Declined"          value={analytics?.recommendations?.declined ?? 0} />
            <StatCard label="Handoffs"          value={analytics?.recommendations?.handoff ?? 0} />
            <StatCard label="Avg match score"   value={analytics?.recommendations?.avg_score ?? 0} sub="0–100" />
            <StatCard label="Featured boost"    value={cfg?.featured_boost || "low"} />
          </div>

          <div className="admin-card">
            <div className="text-sm font-medium text-altura-charcoal">Most recommended items</div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm" data-testid="voice-top-recos">
                <thead className="text-[11px] uppercase tracking-[0.14em] text-altura-charcoal/50">
                  <tr><th className="text-left py-2">Title</th><th className="text-left">Type</th><th className="text-left">Count</th><th className="text-left">Avg Score</th></tr>
                </thead>
                <tbody>
                  {(analytics?.recommendations?.top_recommendations || []).map((t) => (
                    <tr key={t.item_id} className="border-t border-altura-espresso/8">
                      <td className="py-2">{t.title || "-"}</td>
                      <td className="capitalize text-altura-charcoal/70">{t.type}</td>
                      <td>{t.count}</td>
                      <td>{t.avg_score}</td>
                    </tr>
                  ))}
                  {(analytics?.recommendations?.top_recommendations || []).length === 0 &&
                    <tr><td colSpan="4" className="py-6 text-center text-altura-charcoal/50">No recommendations offered yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="admin-card space-y-4" data-testid="voice-reco-controls">
            <div className="text-sm font-medium text-altura-charcoal">Recommendation controls</div>
            <ConfigRow label="Recommendations enabled">
              <input type="checkbox" checked={!!cfg?.recommendations_enabled} onChange={(e) => patchCfg({ recommendations_enabled: e.target.checked })} data-testid="reco-cfg-enabled" />
            </ConfigRow>
            <ConfigRow label="Proactive offers">
              <input type="checkbox" checked={!!cfg?.proactive_recommendations_enabled} onChange={(e) => patchCfg({ proactive_recommendations_enabled: e.target.checked })} data-testid="reco-cfg-proactive" />
            </ConfigRow>
            <ConfigRow label="Max proactive / session">
              <input type="number" className="admin-input w-24" defaultValue={cfg?.max_proactive_offers_per_session ?? 2}
                     onBlur={(e) => patchCfg({ max_proactive_offers_per_session: parseInt(e.target.value) || 2 })} data-testid="reco-cfg-max-proactive" />
            </ConfigRow>
            <ConfigRow label="Minimum score threshold">
              <input type="number" className="admin-input w-24" defaultValue={cfg?.min_recommendation_score ?? 60}
                     onBlur={(e) => patchCfg({ min_recommendation_score: parseInt(e.target.value) || 60 })} data-testid="reco-cfg-minscore" />
            </ConfigRow>
            <ConfigRow label="Max results returned">
              <input type="number" className="admin-input w-24" defaultValue={cfg?.max_recommendation_results ?? 3}
                     onBlur={(e) => patchCfg({ max_recommendation_results: parseInt(e.target.value) || 3 })} data-testid="reco-cfg-maxresults" />
            </ConfigRow>
            <ConfigRow label="Featured boost">
              <select className="admin-select w-40" value={cfg?.featured_boost || "low"} onChange={(e) => patchCfg({ featured_boost: e.target.value })} data-testid="reco-cfg-featured">
                <option value="off">Off (no boost)</option>
                <option value="low">Low (+3)</option>
                <option value="medium">Medium (+8)</option>
                <option value="high">High (+15)</option>
              </select>
            </ConfigRow>
            <ConfigRow label="Article recommendations">
              <input type="checkbox" checked={!!cfg?.article_recommendations_enabled} onChange={(e) => patchCfg({ article_recommendations_enabled: e.target.checked })} data-testid="reco-cfg-articles" />
            </ConfigRow>
            <ConfigRow label="Country recommendations">
              <input type="checkbox" checked={!!cfg?.country_recommendations_enabled} onChange={(e) => patchCfg({ country_recommendations_enabled: e.target.checked })} data-testid="reco-cfg-countries" />
            </ConfigRow>
            <ConfigRow label="Consultation recommendation">
              <input type="checkbox" checked={!!cfg?.consultation_recommendation_enabled} onChange={(e) => patchCfg({ consultation_recommendation_enabled: e.target.checked })} data-testid="reco-cfg-consult" />
            </ConfigRow>
            <ConfigRow label="Disclaimer text">
              <textarea className="admin-input" rows={2} defaultValue={cfg?.recommendation_disclaimer}
                        onBlur={(e) => patchCfg({ recommendation_disclaimer: e.target.value })} data-testid="reco-cfg-disclaimer" />
            </ConfigRow>
          </div>
        </div>
      )}

      {/* SESSIONS */}
      {tab === "sessions" && (        <div className="admin-card">
          <div className="text-sm font-medium text-altura-charcoal">Recent voice sessions</div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="voice-sessions-table">
              <thead className="text-[11px] uppercase tracking-[0.14em] text-altura-charcoal/50">
                <tr><th className="text-left py-2">Started</th><th className="text-left">Page</th><th className="text-left">Voice</th><th className="text-left">Duration</th><th className="text-left">Cost</th><th className="text-left">Lead</th><th className="text-left">Status</th><th /></tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id} className="border-t border-altura-espresso/8 hover:bg-altura-ivory-2/50">
                    <td className="py-2">{(s.started_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="text-altura-charcoal/70">{(s.page_context?.title || s.page_context?.slug || s.source_page || "-").slice(0, 30)}</td>
                    <td className="capitalize">{s.voice || "-"}</td>
                    <td>{fmtSec(s.duration_seconds)}</td>
                    <td>{fmtUsd(s.estimated_cost_usd)}</td>
                    <td>{s.lead_captured ? <span className="badge-gold badge-gold-solid text-[9px]">Lead</span> : <span className="text-altura-charcoal/40">-</span>}</td>
                    <td className="capitalize">{s.status}</td>
                    <td><button className="text-altura-gold-dark hover:underline text-xs" onClick={() => openDetail(s.id)} data-testid={`voice-session-open-${s.id}`}>Replay →</button></td>
                  </tr>
                ))}
                {sessions.length === 0 && <tr><td colSpan="8" className="py-8 text-center text-altura-charcoal/50">No voice sessions yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CONFIG */}
      {tab === "config" && cfg && (
        <div className="admin-card space-y-5" data-testid="voice-config-panel">
          <ConfigRow label="Enabled">
            <input type="checkbox" checked={!!cfg.is_enabled} onChange={(e) => patchCfg({ is_enabled: e.target.checked })} data-testid="voice-cfg-enabled" />
          </ConfigRow>
          <ConfigRow label="Maintenance mode">
            <input type="checkbox" checked={!!cfg.maintenance_mode} onChange={(e) => patchCfg({ maintenance_mode: e.target.checked })} data-testid="voice-cfg-maint" />
          </ConfigRow>
          <ConfigRow label="Default voice">
            <select className="admin-select w-52" value={cfg.default_voice || "sage"} onChange={(e) => patchCfg({ default_voice: e.target.value })} data-testid="voice-cfg-voice">
              {voices.map((v) => <option key={v.id} value={v.id}>{v.label} — {v.description.slice(0, 40)}</option>)}
            </select>
          </ConfigRow>
          <ConfigRow label="Max session (sec)">
            <input type="number" className="admin-input w-32" defaultValue={cfg.max_session_seconds} onBlur={(e) => patchCfg({ max_session_seconds: parseInt(e.target.value) || 900 })} />
          </ConfigRow>
          <ConfigRow label="Daily cap (USD)">
            <input type="number" step="0.5" className="admin-input w-32" defaultValue={cfg.daily_usage_cap_usd} onBlur={(e) => patchCfg({ daily_usage_cap_usd: parseFloat(e.target.value) || 10 })} />
          </ConfigRow>
          <ConfigRow label="Sessions / hour / IP">
            <input type="number" className="admin-input w-24" defaultValue={cfg.rate_limit_sessions_per_hour_per_ip} onBlur={(e) => patchCfg({ rate_limit_sessions_per_hour_per_ip: parseInt(e.target.value) || 3 })} />
          </ConfigRow>
          <ConfigRow label="Idle timeout (sec)">
            <input type="number" className="admin-input w-24" defaultValue={cfg.idle_timeout_seconds} onBlur={(e) => patchCfg({ idle_timeout_seconds: parseInt(e.target.value) || 45 })} />
          </ConfigRow>
          <ConfigRow label="Consent text">
            <textarea className="admin-input" rows={4} defaultValue={cfg.consent_text} onBlur={(e) => patchCfg({ consent_text: e.target.value })} />
          </ConfigRow>
          <ConfigRow label="Default greeting">
            <textarea className="admin-input" rows={3} defaultValue={cfg.greeting_default} onBlur={(e) => patchCfg({ greeting_default: e.target.value })} />
          </ConfigRow>
          <ConfigRow label="Disclaimer">
            <textarea className="admin-input" rows={2} defaultValue={cfg.disclaimer_text} onBlur={(e) => patchCfg({ disclaimer_text: e.target.value })} />
          </ConfigRow>
        </div>
      )}

      {/* VOICE PREVIEW */}
      {tab === "voices" && (
        <div className="admin-card" data-testid="voice-preview-panel">
          <div className="text-sm text-altura-charcoal">
            Preview each supported voice with a short spoken introduction. Talk to test interruption and cadence.
          </div>
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            {voices.map((v) => (
              <div key={v.id} className="p-4 rounded-md border border-altura-espresso/10 bg-white flex items-start justify-between gap-4">
                <div>
                  <div className="font-editorial text-lg text-altura-espresso capitalize">{v.label}</div>
                  <div className="text-xs text-altura-charcoal/60 mt-1">{v.description}</div>
                </div>
                <div className="flex items-center gap-2">
                  {previewState === "live" && previewVoice === v.id ? (
                    <button className="btn-espresso" onClick={stopPreview} data-testid={`voice-preview-stop-${v.id}`}>
                      <Square className="h-3.5 w-3.5" /> Stop
                    </button>
                  ) : (
                    <button className="btn-outline-gold" onClick={() => { setPreviewVoice(v.id); runPreview(v.id); }} disabled={previewState === "connecting"} data-testid={`voice-preview-play-${v.id}`}>
                      <Play className="h-3.5 w-3.5" /> {previewState === "connecting" && previewVoice === v.id ? "Connecting…" : "Test"}
                    </button>
                  )}
                  {cfg?.default_voice !== v.id && (
                    <button className="text-[11px] text-altura-gold-dark hover:underline" onClick={() => patchCfg({ default_voice: v.id })} data-testid={`voice-preview-set-default-${v.id}`}>Set default</button>
                  )}
                  {cfg?.default_voice === v.id && <span className="badge-gold badge-gold-solid text-[9px]">Default</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SESSION DETAIL DRAWER */}
      {detail && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-stretch justify-end" onClick={() => setDetail(null)}>
          <div className="w-full max-w-2xl bg-white h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="voice-session-detail">
            <div className="px-6 py-4 border-b border-altura-espresso/10 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-altura-gold-dark">Voice Session Replay</div>
                <div className="font-editorial text-lg text-altura-espresso">{detail.session?.page_context?.title || detail.session?.source_page || "Session"}</div>
              </div>
              <button onClick={() => setDetail(null)} className="text-altura-charcoal/50" data-testid="voice-detail-close">✕</button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <Meta k="Voice" v={detail.session?.voice || "-"} />
                <Meta k="Duration" v={fmtSec(detail.session?.duration_seconds)} />
                <Meta k="Cost" v={fmtUsd(detail.session?.estimated_cost_usd)} />
                <Meta k="User turns" v={detail.session?.user_turn_count ?? 0} />
                <Meta k="Interruptions" v={detail.session?.interruption_count ?? 0} />
                <Meta k="Lead" v={detail.session?.lead_captured ? "Captured" : "—"} />
              </div>

              {detail.summary && (
                <div className="rounded-md border border-altura-gold/30 bg-altura-gold/5 p-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-altura-gold-dark mb-2">Structured Summary</div>
                  <div className="text-sm text-altura-charcoal whitespace-pre-wrap">{detail.summary.summary_text}</div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <Meta k="Intent" v={detail.summary.intent} />
                    <Meta k="Temperature" v={detail.summary.lead_temperature} />
                    <Meta k="Budget" v={detail.summary.budget} />
                    <Meta k="Timeline" v={detail.summary.timeline} />
                    <Meta k="Countries" v={(detail.summary.countries_discussed || []).join(", ") || "—"} />
                    <Meta k="Opportunities" v={(detail.summary.opportunities_discussed || []).join(", ") || "—"} />
                    <Meta k="Sales stage" v={detail.summary.recommended_sales_stage} />
                    <Meta k="Follow-up" v={detail.summary.recommended_follow_up} />
                  </div>
                  {detail.summary.suggested_email && (
                    <div className="mt-3">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-altura-gold-dark mb-1">Suggested follow-up email</div>
                      <div className="text-xs text-altura-charcoal/85 whitespace-pre-wrap border border-altura-espresso/10 rounded p-3 bg-white">{detail.summary.suggested_email}</div>
                    </div>
                  )}
                </div>
              )}

              {detail.lead && (
                <div className="rounded-md border border-altura-espresso/10 bg-white p-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-altura-gold-dark mb-2">Linked Lead</div>
                  <div className="text-sm">{detail.lead.name} · {detail.lead.email}{detail.lead.phone ? ` · ${detail.lead.phone}` : ""}</div>
                </div>
              )}

              {(detail.recommendations || []).length > 0 && (
                <div className="rounded-md border border-altura-espresso/10 bg-white">
                  <div className="px-4 py-2 border-b border-altura-espresso/10 text-[10px] uppercase tracking-[0.18em] text-altura-gold-dark">Recommendations offered</div>
                  <div className="px-4 py-3 space-y-2">
                    {(detail.recommendations || []).map((r) => (
                      <div key={r.id} className="flex items-start justify-between text-sm">
                        <div>
                          <div className="text-altura-charcoal">{r.title || "-"} <span className="text-[10px] text-altura-charcoal/60 uppercase ml-1">{r.recommendation_type}</span></div>
                          <div className="text-[11px] text-altura-charcoal/60">{(r.match_reasons || []).slice(0, 3).join(" · ")}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[11px] font-mono text-altura-gold-dark">Score {r.score}</div>
                          <div className="text-[10px] uppercase tracking-[0.14em] text-altura-charcoal/60">{r.outcome}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-md border border-altura-espresso/10 bg-white">
                <div className="px-4 py-2 border-b border-altura-espresso/10 text-[10px] uppercase tracking-[0.18em] text-altura-gold-dark">Transcript</div>
                <div className="px-4 py-3 space-y-3 max-h-[50vh] overflow-y-auto">
                  {(detail.transcript || []).map((m) => (
                    <div key={m.id} className={m.role === "user" ? "text-altura-charcoal/70" : "text-altura-charcoal"}>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-altura-gold-dark">{m.role}</div>
                      <div className="text-sm whitespace-pre-wrap">{m.content}</div>
                    </div>
                  ))}
                  {(detail.transcript || []).length === 0 && <div className="text-sm text-altura-charcoal/50">No transcript captured.</div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, accent }) {
  const color = accent === "danger" ? "text-altura-burgundy" : accent === "warn" ? "text-altura-gold-dark" : "text-altura-espresso";
  return (
    <div className="admin-stat">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-altura-charcoal/50">
        <span>{label}</span>{Icon ? <Icon className="h-3.5 w-3.5" /> : null}
      </div>
      <div className={`font-editorial text-2xl ${color}`}>{value}</div>
      {sub ? <div className="text-[11px] text-altura-charcoal/60">{sub}</div> : null}
    </div>
  );
}
function ConfigRow({ label, children }) {
  return (
    <div className="grid gap-2 md:grid-cols-[220px_1fr] items-start md:items-center">
      <div className="text-xs uppercase tracking-[0.14em] text-altura-charcoal/60">{label}</div>
      <div>{children}</div>
    </div>
  );
}
function Meta({ k, v }) {
  return <div><span className="text-[10px] uppercase tracking-[0.14em] text-altura-charcoal/50">{k}</span><div className="text-altura-charcoal">{v || "—"}</div></div>;
}
